import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useCheckoutContext } from '@zooplus/checkout-state';

import { getAppConfig } from '@config/get-app-config';
import { CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';

interface AuthParams {
  [key: string]: string;
}

interface AuthUrls {
  loginUrl: string;
  registrationUrl: string;
}

const buildAuthUrl = (baseUrlWithPath: string, params: AuthParams): string => {
  const url = new URL(`${baseUrlWithPath}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.append(key, value);
    }
  });
  return url.toString();
};

const getBaseUrlWithPath = (urlString: string) => {
  const url = new URL(urlString);
  return url.origin + url.pathname;
};

const useAuthUrls = (): AuthUrls => {
  const location = useLocation();
  const { checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const keycloakData = checkoutState?.stateAttributes?.keycloakData;
  const loginBaseUrlWithPath = getBaseUrlWithPath(keycloakData?.loginBaseUrl as string);
  const registerBaseUrlWithPath = getBaseUrlWithPath(keycloakData?.registrationBaseUrl as string);

  const authParams = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search);
    const params = Object.fromEntries(queryParams?.entries());
    if (!params?.ui_locales) {
      const {
        site: { locale },
      } = getAppConfig();
      params.ui_locales = locale;
    }
    return params;
  }, [location.search]);

  return useMemo(
    () => ({
      loginUrl: buildAuthUrl(loginBaseUrlWithPath, authParams),
      registrationUrl: buildAuthUrl(registerBaseUrlWithPath, authParams),
    }),
    [loginBaseUrlWithPath, registerBaseUrlWithPath, authParams],
  );
};

export default useAuthUrls;
